import React from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Card, CardContent, Typography, Grid } from "@material-ui/core";
import moment from "moment";
import { TextClip } from "frame/components";
import { createRowSelector } from "utils";
import { formatLinkedUsageSource } from "../utils/equipment-usage-display";
import { unitLabel } from "../utils/equipment-usage-math";

const useStyles = makeStyles((theme) => ({
	card: {
		display: "flex",
		flexWrap: "wrap",
		position: "relative",
		marginBottom: theme.spacing(2),
	},
	container: {
		margin: theme.spacing(0.5),
	},
	content: {
		padding: theme.spacing(2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: theme.palette.text.tertiary,
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
}));

const EquipmentUsageListing = ({
	usage,
	showStaff,
	onSelect,
	...rest
}) => {
	const classes = useStyles();
	const onRowSelect = createRowSelector(() => onSelect(usage));
	const linkedSource = formatLinkedUsageSource(usage.source);

	return (
		<Card
			{...rest}
			data-cy={`equipment-usage-${usage.id}`}
			className={clsx(classes.card, rest.className)}
			onClick={onRowSelect}
			style={{ cursor: "pointer" }}
		>
			<CardContent className={classes.content}>
				<Grid container spacing={2} className={classes.container}>
					<Grid item lg={2} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Date
						</Typography>
						<Typography variant="h6" className={classes.value}>
							{usage.usageDate
								? moment(usage.usageDate).format("D MMM YYYY")
								: "-"}
						</Typography>
					</Grid>
					<Grid item lg={showStaff ? 2 : 3} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Equipment
						</Typography>
						<Typography variant="h6" className={classes.value}>
							<TextClip
								hideSeeMore
								text={usage.equipmentName || "-"}
								numberOfCharacters={60}
							/>
						</Typography>
					</Grid>
					{showStaff && (
						<Grid item lg={2} md={6} xs={12}>
							<Typography className={classes.label} variant="body2">
								Staff
							</Typography>
							<Typography variant="h6" className={classes.value}>
								<TextClip
									hideSeeMore
									text={usage.staffName || "-"}
									numberOfCharacters={60}
								/>
							</Typography>
						</Grid>
					)}
					<Grid item lg={2} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Units used
						</Typography>
						<Typography variant="h6" className={classes.value}>
							{usage.unitsUsed} {unitLabel(usage.unitType)}
						</Typography>
					</Grid>
					<Grid item lg={showStaff ? 2 : 3} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Site
						</Typography>
						<Typography variant="h6" className={classes.value}>
							<TextClip
								hideSeeMore
								text={usage.siteName || "-"}
								numberOfCharacters={60}
							/>
						</Typography>
					</Grid>
					{linkedSource && (
						<Grid item lg={2} md={6} xs={12}>
							<Typography className={classes.label} variant="body2">
								Linked
							</Typography>
							<Typography variant="h6" className={classes.value}>
								{linkedSource}
							</Typography>
						</Grid>
					)}
					<Grid item lg={showStaff ? 2 : 3} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Notes
						</Typography>
						<Typography variant="h6" className={classes.value}>
							<TextClip
								hideSeeMore
								text={usage.notes || "-"}
								numberOfCharacters={120}
							/>
						</Typography>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};

EquipmentUsageListing.propTypes = {
	usage: PropTypes.object.isRequired,
	showStaff: PropTypes.bool,
	onSelect: PropTypes.func.isRequired,
};

export default EquipmentUsageListing;
